import * as React from "react";
import { useState, useEffect } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
import Link from "next/link";
import Button from "@src/components/Button";
import { GetAlbumDetailsQuery } from "@src/@types/api.d";

type Props = {
  eventId: number;
  albumDetailsData: GetAlbumDetailsQuery;
  eventSlug: string;
};

export function insertAt(
  array: unknown[],
  index: number,
  ...elementsArray: unknown[]
) {
  array.splice(index, 0, ...elementsArray);
}

const Masonry: React.FC<Props> = ({ eventId, eventSlug, albumDetailsData }) => {
  const mediaData = React.useMemo(
    () => albumDetailsData?.getMedia || [],
    [albumDetailsData]
  );
  const [currentImageSet, setCurrentImageSet] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const isValidImageUrl = (img: any): boolean => {
    return (
      img &&
      img.meta &&
      typeof img.meta.s3_url === "string" &&
      img.meta.s3_url.trim() !== "" &&
      img.meta.s3_url.startsWith("http")
    );
  };

  const imageSets = React.useMemo(() => {
    const validMedia = mediaData.filter(isValidImageUrl);

    if (validMedia.length === 0) return [];

    if (validMedia.length === 3) {
      return [validMedia];
    }

    if (validMedia.length > 3) {
      const sets = [];
      const totalImages = validMedia.length;

      const numSets = Math.ceil(totalImages / 3);

      for (let setIndex = 0; setIndex < numSets; setIndex++) {
        const set = [];
        for (let positionIndex = 0; positionIndex < 3; positionIndex++) {
          const imageIndex = (setIndex * 3 + positionIndex) % totalImages;
          set.push(validMedia[imageIndex]);
        }
        sets.push(set);
      }

      return sets;
    }

    return [];
  }, [mediaData]);

  useEffect(() => {
    if (imageSets.length <= 1) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentImageSet((prev) => (prev + 1) % imageSets.length);
        setIsAnimating(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, [imageSets.length]);

  const handleImageSetChange = (index: number) => {
    if (index === currentImageSet || isAnimating) return;

    setIsAnimating(true);
    setTimeout(() => {
      setCurrentImageSet(index);
      setIsAnimating(false);
    }, 300);
  };

  const currentSet = imageSets[currentImageSet] || [];

  if (
    !albumDetailsData?.getMedia ||
    albumDetailsData.getMedia.length === 0 ||
    imageSets.length === 0 ||
    currentSet.length === 0
  ) {
    return null;
  }

  return (
    <div className="bg-white lg:pt-[8rem] pt-[5rem] pb-[3rem] px-4 relative">
      <div className="max-w-6xl mx-auto">
        {/* Large Screens */}
        <div
          className={`hidden lg:flex flex-row gap-10 items-center justify-center mb-8 transition-all duration-500 ${
            isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"
          }`}
        >
          <div className="flex flex-col gap-4 justify-between items-end w-[40%]">
            {/* First Image */}
            {currentSet[0] && isValidImageUrl(currentSet[0]) && (
              <div className="relative w-full aspect-[4/3] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                <Image
                  placeholder="blur"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(700, 475)
                  )}`}
                  src={currentSet[0]?.meta?.s3_url || ""}
                  layout="fill"
                  alt={`Gallery image ${currentImageSet * 3 + 1}`}
                  objectFit="cover"
                  className="rounded-2xl object-center shadow-2xl"
                  onError={(e) => {
                    console.error(
                      "Image load error:",
                      currentSet[0]?.meta?.s3_url
                    );
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            )}

            {/* Second Image */}
            {currentSet[1] && isValidImageUrl(currentSet[1]) && (
              <div className="relative w-full aspect-[4/3] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                <Image
                  placeholder="blur"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(700, 475)
                  )}`}
                  src={currentSet[1]?.meta?.s3_url || ""}
                  layout="fill"
                  alt={`Gallery image ${currentImageSet * 3 + 2}`}
                  objectFit="cover"
                  className="rounded-2xl object-center shadow-2xl"
                  onError={(e) => {
                    console.error(
                      "Image load error:",
                      currentSet[1]?.meta?.s3_url
                    );
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            )}
          </div>

          <div className="w-[40%] mb-7">
            {/* Third Image */}
            {currentSet[2] && isValidImageUrl(currentSet[2]) && (
              <div className="relative w-full aspect-[3/4] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                <Image
                  placeholder="blur"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(700, 475)
                  )}`}
                  src={currentSet[2]?.meta?.s3_url || ""}
                  layout="fill"
                  alt={`Gallery image ${currentImageSet * 3 + 3}`}
                  objectFit="cover"
                  className="rounded-2xl object-center shadow-2xl"
                  onError={(e) => {
                    console.error(
                      "Image load error:",
                      currentSet[2]?.meta?.s3_url
                    );
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            )}
          </div>
        </div>

        {/* Mobile */}
        <div
          className={`lg:hidden flex flex-col gap-4 items-center mb-8 transition-all duration-500 ${
            isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"
          }`}
        >
          {/* First Half Image */}
          {currentSet[0] && isValidImageUrl(currentSet[0]) && (
            <div className="relative w-[60%] aspect-[4/3] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
              <Image
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(700, 475)
                )}`}
                src={currentSet[0]?.meta?.s3_url || ""}
                layout="fill"
                alt={`Gallery image ${currentImageSet * 3 + 1}`}
                objectFit="cover"
                className="rounded-2xl object-center shadow-2xl"
                onError={(e) => {
                  console.error(
                    "Image load error:",
                    currentSet[0]?.meta?.s3_url
                  );
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
            </div>
          )}

          {/* Full Image */}
          {currentSet[2] && isValidImageUrl(currentSet[2]) && (
            <div className="relative w-full aspect-[4/3] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
              <Image
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(700, 475)
                )}`}
                src={currentSet[2]?.meta?.s3_url || ""}
                layout="fill"
                alt={`Gallery image ${currentImageSet * 3 + 3}`}
                objectFit="cover"
                className="rounded-2xl object-center shadow-2xl"
                onError={(e) => {
                  console.error(
                    "Image load error:",
                    currentSet[2]?.meta?.s3_url
                  );
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
            </div>
          )}

          {/* Second Half Image */}
          {currentSet[1] && isValidImageUrl(currentSet[1]) && (
            <div className="relative w-[60%] aspect-[4/3] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
              <Image
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(700, 475)
                )}`}
                src={currentSet[1]?.meta?.s3_url || ""}
                layout="fill"
                alt={`Gallery image ${currentImageSet * 3 + 2}`}
                objectFit="cover"
                className="rounded-2xl object-center shadow-2xl"
                onError={(e) => {
                  console.error(
                    "Image load error:",
                    currentSet[1]?.meta?.s3_url
                  );
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
            </div>
          )}
        </div>

        {/* Image set indicators */}
        {imageSets.length > 1 && (
          <div className="flex justify-center gap-3 mb-8">
            {imageSets.map((_, index) => (
              <button
                key={index}
                onClick={() => handleImageSetChange(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentImageSet
                    ? "bg-black scale-125 shadow-lg"
                    : "bg-black/40 hover:bg-black/70"
                }`}
                aria-label={`View image set ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Button */}
      {eventSlug && (
        <div className="flex flex-col md:flex-row gap-8 justify-center items-center">
          <Link
            href={`https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`}
            passHref
          >
            <a target="_blank" rel="noopener noreferrer">
              <button
                className="px-8 py-3 rounded-full transition-all duration-300 border-2 border-black text-black text-xl font-corGaramond font-bold hover:bg-black hover:text-white transform hover:scale-105 shadow-lg hover:shadow-xl backdrop-blur-sm"
                type="button"
              >
                See Full Photobook
              </button>
            </a>
          </Link>
        </div>
      )}

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${
              mediaData.length * 320 + (mediaData.length - 1) * 24
            }px);
          }
        }
        
        .animate-scroll {
          animation: scroll ${mediaData.length * 4}s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default Masonry;
